/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X } from 'lucide-react';

interface MermaidDiagramProps {
  chart: string;
  isDark?: boolean;
}

type MermaidRenderState =
  | { status: 'loading'; svg?: undefined; error?: undefined }
  | { status: 'rendered'; svg: string; error?: undefined }
  | { status: 'error'; svg?: undefined; error: string };

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown Mermaid rendering error');
};

const MermaidDiagram = memo(function MermaidDiagram({
  chart,
  isDark = false,
}: MermaidDiagramProps) {
  const reactId = useId();
  const baseId = useMemo(
    () => `mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`,
    [reactId],
  );
  const renderCountRef = useRef(0);
  const normalizedChart = useMemo(() => String(chart || '').trim(), [chart]);
  const [renderState, setRenderState] = useState<MermaidRenderState>({ status: 'loading' });
  const [isExpanded, setIsExpanded] = useState(false);

  const closeExpanded = useCallback(() => setIsExpanded(false), []);
  const openExpanded = useCallback(() => {
    if (renderState.status === 'rendered') {
      setIsExpanded(true);
    }
  }, [renderState.status]);

  useEffect(() => {
    if (!normalizedChart) {
      setRenderState({ status: 'error', error: 'Diagram source is empty.' });
      return undefined;
    }

    let isCancelled = false;
    const renderCount = renderCountRef.current + 1;
    renderCountRef.current = renderCount;
    setRenderState({ status: 'loading' });

    async function renderDiagram() {
      try {
        const mermaidModule = await import('mermaid');
        const mermaid = mermaidModule.default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: isDark ? 'dark' : 'default',
          flowchart: {
            curve: 'basis',
            htmlLabels: true,
          },
        });

        const { svg } = await mermaid.render(`${baseId}-${renderCount}`, normalizedChart);
        if (!isCancelled && renderCountRef.current === renderCount) {
          setRenderState({ status: 'rendered', svg });
        }
      } catch (error) {
        if (!isCancelled && renderCountRef.current === renderCount) {
          setRenderState({ status: 'error', error: getErrorMessage(error) });
        }
      }
    }

    renderDiagram();

    return () => {
      isCancelled = true;
    };
  }, [baseId, isDark, normalizedChart]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeExpanded();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeExpanded, isExpanded]);

  const shellStyle: React.CSSProperties = {
    margin: '10px 0',
    border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.12)'}`,
    borderRadius: 8,
    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.72)' : '#f8fafc',
    overflowX: 'auto',
    padding: 12,
    position: 'relative',
  };

  const iconButtonStyle: React.CSSProperties = {
    alignItems: 'center',
    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.88)' : 'rgba(255, 255, 255, 0.92)',
    border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.36)' : 'rgba(15, 23, 42, 0.14)'}`,
    borderRadius: 8,
    color: isDark ? '#e5e7eb' : '#0f172a',
    cursor: 'pointer',
    display: 'inline-flex',
    height: 34,
    justifyContent: 'center',
    padding: 0,
    width: 34,
  };

  if (renderState.status === 'loading') {
    return (
      <div style={shellStyle}>
        <div
          style={{
            color: isDark ? '#cbd5e1' : '#475569',
            fontSize: 13,
          }}
        >
          Rendering diagram...
        </div>
      </div>
    );
  }

  if (renderState.status === 'error') {
    return (
      <div style={shellStyle}>
        <div
          role="alert"
          style={{
            color: isDark ? '#fca5a5' : '#b91c1c',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Could not render Mermaid diagram.
        </div>
        <div
          style={{
            color: isDark ? '#cbd5e1' : '#475569',
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          {renderState.error}
        </div>
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 12,
            color: isDark ? '#e5e7eb' : '#1f2937',
          }}
        >
          {normalizedChart}
        </pre>
      </div>
    );
  }

  const expandedPortal = isExpanded && renderState.status === 'rendered' && typeof document !== 'undefined'
    ? createPortal(
      <div
        aria-modal="true"
        role="dialog"
        onClick={closeExpanded}
        style={{
          alignItems: 'center',
          backgroundColor: isDark ? 'rgba(2, 6, 23, 0.86)' : 'rgba(15, 23, 42, 0.5)',
          display: 'flex',
          inset: 0,
          justifyContent: 'center',
          padding: 'clamp(16px, 4vw, 48px)',
          position: 'fixed',
          zIndex: 1600,
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            backgroundColor: isDark ? '#0f172a' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.34)' : 'rgba(15, 23, 42, 0.12)'}`,
            borderRadius: 10,
            boxShadow: isDark
              ? '0 24px 80px rgba(0, 0, 0, 0.55)'
              : '0 24px 80px rgba(15, 23, 42, 0.22)',
            maxHeight: 'calc(100vh - 48px)',
            maxWidth: 'calc(100vw - 48px)',
            overflow: 'auto',
            padding: '44px 18px 18px',
            position: 'relative',
          }}
        >
          <button
            aria-label="Close diagram"
            onClick={closeExpanded}
            style={{
              ...iconButtonStyle,
              position: 'absolute',
              right: 10,
              top: 10,
            }}
            title="Close diagram"
            type="button"
          >
            <X size={18} strokeWidth={2} />
          </button>
          <style>
            {`
              .paxia-mermaid-diagram--expanded svg {
                display: block;
                height: auto;
                margin: 0 auto;
                max-width: none;
                min-width: min(1100px, calc(100vw - 96px));
                width: auto;
              }
            `}
          </style>
          <div
            className="paxia-mermaid-diagram--expanded"
            dangerouslySetInnerHTML={{ __html: renderState.svg }}
          />
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <div
        onClick={openExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openExpanded();
          }
        }}
        role="button"
        style={{
          ...shellStyle,
          cursor: 'zoom-in',
        }}
        tabIndex={0}
      >
      <style>
        {`
          .paxia-mermaid-diagram svg {
            display: block;
            height: auto;
            margin: 0 auto;
            max-width: 100%;
          }
        `}
      </style>
      <button
        aria-label="Expand diagram"
        onClick={(event) => {
          event.stopPropagation();
          openExpanded();
        }}
        style={{
          ...iconButtonStyle,
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
        }}
        title="Expand diagram"
        type="button"
      >
        <Maximize2 size={17} strokeWidth={2} />
      </button>
      <div
        className="paxia-mermaid-diagram"
        dangerouslySetInnerHTML={{ __html: renderState.svg }}
      />
      </div>
      {expandedPortal}
    </>
  );
});

export default MermaidDiagram;
